import TaskAutomationService from './taskAutomationService.js';
import NotificationService from './notificationService.js';
import SmartTaskService from './smartTaskService.js';
import { logHelpfulError } from '../utils/firebaseIndexHelper.js';

/**
 * Automation Manager - Chunk 3 Implementation
 * Coordinates all automation services and provides unified interface
 */

class AutomationManager {
  constructor(db) {
    this.db = db;
    this.taskAutomation = new TaskAutomationService(db);
    this.notifications = new NotificationService(db);
    this.smartTasks = new SmartTaskService(db);
    this.activeIncidents = new Set();
    this.userSessions = new Map();
  }

  /**
   * Initialize automation for an incident
   */
  async initializeIncidentAutomation(incidentId, orgId, userId, userRole) {
    try {
      console.log(`Initializing automation for incident ${incidentId}`);

      // Start task monitoring
      await this.taskAutomation.startTaskMonitoring(incidentId, orgId);

      // Start notification listener for user
      this.notifications.startNotificationListener(userId, userRole, orgId, (notifications) => {
        this.handleNotificationUpdate(userId, notifications);
      });

      // Track active incident
      this.activeIncidents.add(incidentId);

      // Store user session info
      this.userSessions.set(userId, {
        incidentId,
        orgId,
        userRole,
        startTime: new Date()
      });

      // Perform initial smart analysis
      await this.performInitialAnalysis(incidentId, orgId);

      console.log(`Automation initialized successfully for incident ${incidentId}`);
      return true;

    } catch (error) {
      console.error('Error initializing incident automation:', error);
      return false;
    }
  }

  /**
   * Shutdown automation for an incident
   */
  async shutdownIncidentAutomation(incidentId, userId) {
    try {
      console.log(`Shutting down automation for incident ${incidentId}`);

      // Stop task monitoring
      this.taskAutomation.stopTaskMonitoring(incidentId);

      // Get user session info
      const userSession = this.userSessions.get(userId);
      if (userSession) {
        // Stop notification listener
        this.notifications.stopNotificationListener(userId, userSession.userRole, userSession.orgId);
        
        // Remove user session
        this.userSessions.delete(userId);
      }

      // Remove from active incidents
      this.activeIncidents.delete(incidentId);

      console.log(`Automation shutdown complete for incident ${incidentId}`);
      return true;

    } catch (error) {
      console.error('Error shutting down incident automation:', error);
      return false;
    }
  }

  /**
   * Perform initial smart analysis when automation starts
   */
  async performInitialAnalysis(incidentId, orgId) {
    try {
      // Analyze current team performance
      const teamAnalysis = await this.smartTasks.analyzeTeamPerformance(incidentId, orgId);
      
      // Check for missing critical tasks
      const smartTasks = await this.smartTasks.createSmartTasks(incidentId, orgId);
      
      // Suggest workload rebalancing if needed
      const rebalancingSuggestions = await this.smartTasks.suggestWorkloadRebalancing(incidentId, orgId);

      // Create notifications for significant findings
      if (teamAnalysis.bottlenecks.length > 0) {
        await this.notifications.createNotification({
          incidentId,
          orgId,
          type: 'team_analysis',
          priority: 'important',
          targetRole: 'cmt_leader',
          title: 'Team Performance Analysis',
          message: `${teamAnalysis.bottlenecks.length} role(s) are overloaded. Review workload distribution.`,
          actionRequired: true,
          analysisData: teamAnalysis
        });
      }

      if (smartTasks.length > 0) {
        await this.notifications.createNotification({
          incidentId,
          orgId,
          type: 'smart_tasks_available',
          priority: 'normal',
          targetRole: 'cmt_leader',
          title: 'Smart Task Suggestions Available',
          message: `${smartTasks.length} additional task(s) suggested based on incident type.`,
          actionRequired: true,
          suggestedTasks: smartTasks.slice(0, 3) // Show first 3
        });
      }

      if (rebalancingSuggestions.length > 0) {
        await this.notifications.createNotification({
          incidentId,
          orgId,
          type: 'rebalancing_suggestions',
          priority: 'normal',
          targetRole: 'cmt_leader',
          title: 'Workload Rebalancing Suggestions',
          message: `${rebalancingSuggestions.length} task(s) could be redistributed for better balance.`,
          actionRequired: true,
          suggestions: rebalancingSuggestions
        });
      }

    } catch (error) {
      logHelpfulError(error, 'Automation Manager - performInitialAnalysis');
    }
  }

  /**
   * Handle notification updates for users
   */
  handleNotificationUpdate(userId, notifications) {
    // This could trigger UI updates, websocket messages, etc.
    console.log(`User ${userId} has ${notifications.length} notifications`);
    
    // Store latest notifications for user
    this.userSessions.set(userId, {
      ...this.userSessions.get(userId),
      lastNotifications: notifications,
      lastNotificationUpdate: new Date()
    });
  }

  /**
   * Get smart task assignment suggestion
   */
  async getTaskAssignmentSuggestion(task, incidentId, orgId) {
    try {
      return await this.smartTasks.suggestTaskAssignment(task, incidentId, orgId);
    } catch (error) {
      console.error('Error getting task assignment suggestion:', error);
      return {
        recommendedRole: 'cmt_leader',
        confidence: 0.5,
        reasoning: 'Default assignment due to error',
        alternatives: [],
        workloadImpact: 'unknown'
      };
    }
  }

  /**
   * Create smart tasks for incident
   */
  async createSmartTasksForIncident(incidentId, orgId) {
    try {
      return await this.smartTasks.createSmartTasks(incidentId, orgId);
    } catch (error) {
      console.error('Error creating smart tasks:', error);
      return [];
    }
  }

  /**
   * Get workload analysis
   */
  async getWorkloadAnalysis(incidentId, orgId) {
    try {
      const currentWorkload = await this.smartTasks.getCurrentWorkload(incidentId, orgId);
      const teamAnalysis = await this.smartTasks.analyzeTeamPerformance(incidentId, orgId);
      const rebalancingSuggestions = await this.smartTasks.suggestWorkloadRebalancing(incidentId, orgId);

      return {
        currentWorkload,
        teamAnalysis,
        rebalancingSuggestions
      };
    } catch (error) {
      console.error('Error getting workload analysis:', error);
      return {
        currentWorkload: {},
        teamAnalysis: { overallUtilization: 0, bottlenecks: [], underutilized: [], recommendations: [] },
        rebalancingSuggestions: []
      };
    }
  }

  /**
   * Get notification statistics for user
   */
  async getNotificationStats(userId) {
    try {
      const userSession = this.userSessions.get(userId);
      if (!userSession) return null;

      return await this.notifications.getNotificationStats(userSession.userRole, userSession.orgId);
    } catch (error) {
      console.error('Error getting notification stats:', error);
      return null;
    }
  }

  /**
   * Mark notification as read
   */
  async markNotificationAsRead(notificationId) {
    try {
      await this.notifications.markAsRead(notificationId);
      return true;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      return false;
    }
  }

  /**
   * Mark all notifications as read for user
   */
  async markAllNotificationsAsRead(userId) {
    try {
      const userSession = this.userSessions.get(userId);
      if (!userSession) return false;

      await this.notifications.markAllAsRead(userSession.userRole, userSession.orgId);
      return true;
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      return false;
    }
  }

  /**
   * Create custom notification
   */
  async createCustomNotification(notification) {
    try {
      await this.notifications.createNotification(notification);
      return true;
    } catch (error) {
      console.error('Error creating custom notification:', error);
      return false;
    }
  }

  /**
   * Get automation status for incident
   */
  getAutomationStatus(incidentId) {
    return {
      isActive: this.activeIncidents.has(incidentId),
      taskMonitoring: this.taskAutomation.activeMonitors.has(incidentId),
      activeUsers: Array.from(this.userSessions.values()).filter(session => 
        session.incidentId === incidentId
      ).length
    };
  }

  /**
   * Get all active incidents with automation
   */
  getActiveIncidents() {
    return Array.from(this.activeIncidents);
  }

  /**
   * Get user session information
   */
  getUserSession(userId) {
    return this.userSessions.get(userId) || null;
  }

  /**
   * Update user session
   */
  updateUserSession(userId, updates) {
    const currentSession = this.userSessions.get(userId);
    if (currentSession) {
      this.userSessions.set(userId, {
        ...currentSession,
        ...updates,
        lastUpdate: new Date()
      });
    }
  }

  /**
   * Cleanup old data and optimize performance
   */
  async performMaintenance() {
    try {
      console.log('Performing automation maintenance...');

      // Clean up old notifications for all active organisations
      const activeOrgs = new Set();
      this.userSessions.forEach(session => activeOrgs.add(session.orgId));

      for (const orgId of activeOrgs) {
        await this.notifications.cleanupOldNotifications(orgId);
      }

      // Remove stale user sessions (older than 24 hours)
      const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
      for (const [userId, session] of this.userSessions.entries()) {
        if (session.startTime < twentyFourHoursAgo) {
          this.userSessions.delete(userId);
          console.log(`Removed stale session for user ${userId}`);
        }
      }

      console.log('Automation maintenance completed');
    } catch (error) {
      console.error('Error during automation maintenance:', error);
    }
  }

  /**
   * Get automation metrics and statistics
   */
  getAutomationMetrics() {
    const now = new Date();
    const sessions = Array.from(this.userSessions.values());

    return {
      activeIncidents: this.activeIncidents.size,
      activeSessions: sessions.length,
      taskMonitors: this.taskAutomation.activeMonitors.size,
      notificationListeners: this.notifications.activeListeners.size,
      averageSessionDuration: sessions.length > 0 ? 
        sessions.reduce((sum, session) => sum + (now - session.startTime), 0) / sessions.length / 1000 / 60 : 0, // minutes
      sessionsByRole: sessions.reduce((acc, session) => {
        acc[session.userRole] = (acc[session.userRole] || 0) + 1;
        return acc;
      }, {}),
      uptime: now - (this.startTime || now)
    };
  }

  /**
   * Initialize the automation manager
   */
  initialize() {
    this.startTime = new Date();
    
    // Set up periodic maintenance (every hour)
    this.maintenanceInterval = setInterval(() => {
      this.performMaintenance();
    }, 60 * 60 * 1000); // 1 hour

    console.log('Automation Manager initialized');
  }

  /**
   * Shutdown the automation manager
   */
  shutdown() {
    // Clear maintenance interval
    if (this.maintenanceInterval) {
      clearInterval(this.maintenanceInterval);
    }

    // Shutdown all active monitoring
    for (const incidentId of this.activeIncidents) {
      this.taskAutomation.stopTaskMonitoring(incidentId);
    }

    // Clear all user sessions
    for (const [userId, session] of this.userSessions.entries()) {
      this.notifications.stopNotificationListener(userId, session.userRole, session.orgId);
    }

    // Clear all data
    this.activeIncidents.clear();
    this.userSessions.clear();

    console.log('Automation Manager shutdown complete');
  }
}

// Create singleton instance
let automationManagerInstance = null;

export const getAutomationManager = (db) => {
  if (!automationManagerInstance) {
    automationManagerInstance = new AutomationManager(db);
    automationManagerInstance.initialize();
  }
  return automationManagerInstance;
};

export default AutomationManager; 